import { Controller } from '@hotwired/stimulus'

// View Transitions between pages. Sits on <body>, so it is present on every page a transition
// can start from or land on.
//
// Turbo wraps each page render in document.startViewTransition (the view-transition meta in the
// head), and elements sharing a view-transition-name on both pages morph into each other. The
// intro logo and the navbar logo carry that name in the markup (motion.css), so the logo flies
// into the navbar as the timeline opens.
//
// Some visits also carry a direction on <html data-transition> for motion.css to key off — so
// far only the intro page's dive. The marker lives just for the visit to the URL it was set for:
// turbo:load clears it once the transition has finished, and a visit elsewhere (this one was
// cancelled), a failed fetch of its URL, or a page shown from the back/forward cache end it too,
// so no later visit picks up a direction by mistake.
//
// It also reveals images that fade in once loaded: the load and error listeners, plus a scan on
// connect for those already complete (the controller connects again with every new body).
export default class extends Controller {
  connect () {
    this.onVisit = (event) => {
      this.location = event.detail.url
      if (event.detail.url !== this.directedUrl) this.#undirect()
    }
    this.onDirectionFailed = () => this.#undirect()
    this.onFetchFailed = (event) => {
      if (String(event.detail.request?.url) === this.directedUrl) this.#undirect()
    }
    this.onLoad = () => this.#clear()
    this.onImage = (event) => this.#reveal(event.target)
    this.onBeforeRender = (event) => this.#nameCardAt(event.detail.newBody, event.detail.renderMethod)

    document.addEventListener('turbo:visit', this.onVisit)
    document.addEventListener('turbo:before-render', this.onBeforeRender)
    document.addEventListener('turbo:load', this.onLoad)
    document.addEventListener('turbo:fetch-request-error', this.onFetchFailed)
    window.addEventListener('pageshow', this.onDirectionFailed)
    document.addEventListener('load', this.onImage, true)
    document.addEventListener('error', this.onImage, true)
    document.documentElement.setAttribute('data-motion', '')
    this.#revealLoaded()
  }

  disconnect () {
    document.removeEventListener('turbo:visit', this.onVisit)
    document.removeEventListener('turbo:before-render', this.onBeforeRender)
    document.removeEventListener('turbo:load', this.onLoad)
    document.removeEventListener('turbo:fetch-request-error', this.onFetchFailed)
    window.removeEventListener('pageshow', this.onDirectionFailed)
    document.removeEventListener('load', this.onImage, true)
    document.removeEventListener('error', this.onImage, true)
    document.documentElement.removeAttribute('data-motion')
  }

  dive (event) {
    if (!this.#plain(event)) return

    this.#direct('dive', event.currentTarget.href)
  }

  mark (event) {
    if (!this.#plain(event)) return

    const link = event.currentTarget
    const card = link.closest('.life_events__item')
    if (!card) return

    this.#clear()
    this.#name(link)
    window.history.replaceState(window.history.state, '', `#${card.id}`)
  }

  forward (event) {
    if (this.#plain(event)) this.#direct('forward', event.currentTarget.href, this.#pendingSlide(event))
  }

  back (event) {
    if (this.#plain(event)) this.#direct('back', event.currentTarget.href, this.#pendingSlide(event))
  }

  slide ({ detail: { direction, url, x, rotate } }) {
    this.#direct(direction, url, { x, rotate })
  }

  #pendingSlide (event) {
    return new URL(event.currentTarget.href, document.baseURI).href === this.directedUrl ? this.slideOffset : undefined
  }

  #plain (event) {
    return !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)
  }

  #direct (kind, url, { x = 0, rotate = 0 } = {}) {
    this.directedUrl = new URL(url, document.baseURI).href
    this.slideOffset = { x, rotate }
    const root = document.documentElement
    root.dataset.transition = kind

    const article = document.querySelector('.life_event__article')
    if (!article || kind === 'dive') return

    let top = 0
    for (let element = article; element; element = element.offsetParent) top += element.offsetTop
    root.style.setProperty('--event-card-top', `${top}px`)
    root.style.setProperty('--event-card-x', `${x}px`)
    root.style.setProperty('--event-card-y', `${-window.scrollY}px`)
    root.style.setProperty('--event-card-rotate', `${rotate}deg`)
    root.style.setProperty('--event-card-old-origin', `${window.innerHeight - top + window.scrollY}px`)
    root.style.setProperty('--event-card-new-origin', `${window.innerHeight - top}px`)
  }

  #undirect () {
    this.directedUrl = null
    this.slideOffset = undefined
    const root = document.documentElement
    if (['dive', 'forward', 'back'].includes(root.dataset.transition)) delete root.dataset.transition
    for (const name of ['top', 'x', 'y', 'rotate', 'old-origin', 'new-origin']) root.style.removeProperty(`--event-card-${name}`)
    if (!root.style.length) root.removeAttribute('style')
  }

  #nameCardAt (body, renderMethod) {
    if (renderMethod !== 'replace' || !this.location) return

    const id = new URL(this.location, document.baseURI).hash.slice(1)
    const link = id && body.querySelector(`#${window.CSS.escape(id)} [data-action~="page-transition#mark"]`)
    if (link) this.#name(link)
  }

  #name (link) {
    const card = link.closest('.life_events__item')
    if (!card) return

    card.style.viewTransitionName = 'event-card'
    const title = card.querySelector('.life_events__item_title')
    if (title) title.style.viewTransitionName = 'event-title'
  }

  #clear () {
    this.#undirect()
    document.querySelectorAll('[style*="view-transition-name: event-"]').forEach((element) => {
      element.style.viewTransitionName = ''
      if (!element.style.length) element.removeAttribute('style')
    })
  }

  #revealLoaded () {
    document.querySelectorAll('img.fade_image').forEach((image) => {
      if (image.complete) this.#reveal(image)
    })
  }

  #reveal (target) {
    if (target instanceof window.HTMLImageElement && target.classList.contains('fade_image')) {
      target.classList.add('fade_image-loaded')
    }
  }
}
